import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const steps = ["Your account", "Cultural identity", "Your phone"];

const Register = () => {
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const [f, setF] = useState({
    name: "", age: "", email: "", password: "",
    ethnicity: "", cultural_background: "", country: "",
    phone: "", emergency_contact_name: "", emergency_contact_phone: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const next = () => {
    setError("");
    if (step === 0 && (!f.name || !f.age || !f.email || f.password.length < 6)) {
      setError("Please complete all fields. Password must be at least 6 characters.");
      return;
    }
    if (step === 1 && (!f.ethnicity || !f.cultural_background || !f.country)) {
      setError("Please share your cultural details so Aurea can honor your background.");
      return;
    }
    setStep(step + 1);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.phone) {
      setError("Please add your phone number so we can verify your account.");
      return;
    }
    if (!agreed) {
      setError("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...f, age: parseInt(f.age, 10), terms_accepted: agreed });
      login(data.token, data.user);
      navigate("/verify");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Unable to create account.");
    } finally {
      setLoading(false);
    }
  };

  const input = "rounded-2xl bg-linen border-linen-3 h-12";

  return (
    <div className="min-h-screen bg-linen flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <Link to="/" className="font-serif text-4xl text-brand block text-center mb-1">Aurea</Link>
        <p className="text-center text-ink-mute mb-6">Let's create your sanctuary, gently.</p>

        <div className="flex gap-2 mb-6">
          {steps.map((s, i) => (
            <div key={i} className="flex-1">
              <div className={`h-1.5 rounded-full ${i <= step ? "bg-brand" : "bg-linen-3"}`} />
              <p className={`text-xs mt-2 ${i === step ? "text-brand font-medium" : "text-ink-mute"}`}>{s}</p>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="bg-white rounded-3xl border border-linen-3 p-8 space-y-5" data-testid="register-form">
          {error && <div className="bg-warmsoft text-warmsoft-text text-sm rounded-2xl px-4 py-3" data-testid="register-error">{error}</div>}

          {step === 0 && (
            <>
              <div className="space-y-2"><Label className="text-ink-2">Full name</Label>
                <Input required value={f.name} onChange={set("name")} className={input} data-testid="reg-name" /></div>
              <div className="space-y-2"><Label className="text-ink-2">Age</Label>
                <Input type="number" min="13" required value={f.age} onChange={set("age")} className={input} data-testid="reg-age" /></div>
              <div className="space-y-2"><Label className="text-ink-2">Email</Label>
                <Input type="email" required value={f.email} onChange={set("email")} className={input} data-testid="reg-email" /></div>
              <div className="space-y-2"><Label className="text-ink-2">Password</Label>
                <Input type="password" required value={f.password} onChange={set("password")} className={input} data-testid="reg-password" /></div>
            </>
          )}

          {step === 1 && (
            <>
              <p className="text-sm text-ink-2 -mt-1">Aurea uses this to give culturally respectful guidance. Never to judge.</p>
              <div className="space-y-2"><Label className="text-ink-2">Ethnicity</Label>
                <Input required value={f.ethnicity} onChange={set("ethnicity")} placeholder="e.g. Nigerian, Korean, Latino, mixed…" className={input} data-testid="reg-ethnicity" /></div>
              <div className="space-y-2"><Label className="text-ink-2">Cultural background</Label>
                <Textarea required value={f.cultural_background} onChange={set("cultural_background")} placeholder="Traditions, faith, values, language that matter to you…" className="rounded-2xl bg-linen border-linen-3 min-h-[90px]" data-testid="reg-culture" /></div>
              <div className="space-y-2"><Label className="text-ink-2">Country / region</Label>
                <Input required value={f.country} onChange={set("country")} placeholder="United States, United Kingdom, India…" className={input} data-testid="reg-country" /></div>
            </>
          )}

          {step === 2 && (
            <>
              <p className="text-sm text-ink-2 -mt-1">We use your phone to verify your account and keep your safety features active. You can add an emergency contact later in Settings.</p>
              <div className="space-y-2"><Label className="text-ink-2">Your phone (with country code)</Label>
                <Input required value={f.phone} onChange={set("phone")} placeholder="+1 555 000 1234" className={input} data-testid="reg-phone" /></div>
              <label className="flex items-start gap-3 pt-1 cursor-pointer" data-testid="reg-terms-label">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#7a5c48] shrink-0"
                  data-testid="reg-terms-checkbox"
                />
                <span className="text-sm text-ink-2 leading-relaxed">
                  I agree to Aurea's{" "}
                  <Link to="/terms" target="_blank" className="text-accent font-medium underline">Terms of Service</Link>{" "}
                  and{" "}
                  <Link to="/privacy" target="_blank" className="text-accent font-medium underline">Privacy Policy</Link>.
                </span>
              </label>
            </>
          )}

          <div className="flex gap-3 pt-2">
            {step > 0 && (
              <Button type="button" variant="outline" onClick={() => setStep(step - 1)} className="rounded-full border-brand text-brand flex-1 h-12" data-testid="reg-back">Back</Button>
            )}
            {step < 2 ? (
              <Button type="button" onClick={next} className="rounded-full bg-brand hover:bg-brand-hover text-linen flex-1 h-12" data-testid="reg-next">Continue</Button>
            ) : (
              <Button type="submit" disabled={loading} className="rounded-full bg-brand hover:bg-brand-hover text-linen flex-1 h-12" data-testid="reg-submit">{loading ? "Creating…" : "Create account"}</Button>
            )}
          </div>
        </form>
        <p className="text-center text-sm text-ink-2 mt-6">
          Already have an account? <Link to="/login" className="text-accent font-medium">Sign in</Link>
        </p>
        <p className="text-center text-[11px] text-ink-mute mt-4">© {new Date().getFullYear()} Aurea · Damian Alan Moses</p>
      </div>
    </div>
  );
};

export default Register;
